import { useEffect, useState, useMemo } from "react";
import api, { errorMessage } from "../lib/api";
import { parseLocaleNum } from "../lib/locale";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Search, Plus, Pencil, Trash2, Loader2, Inbox, X, AlertTriangle, Package } from "lucide-react";
import { toast } from "sonner";
import { formatRupiah, formatTanggal } from "../lib/format";
import { PageHeader } from "../components/ui/page-header";
import { EmptyState } from "../components/ui/empty-state";

const PAGE_SIZE = 10;
const today = () => new Date().toISOString().slice(0, 10);
const emptyItem = { produk_id: "", nama_produk_snapshot: "", jumlah_pcs: 0, harga_per_pcs_snapshot: 0 };
const emptyForm = { spk_id: "", tanggal_setor: today(), items: [{ ...emptyItem }], catatan: "" };

export default function SetorBarangPage() {
    const { canWrite, canDelete } = useAuth();
    const [items, setItems] = useState([]);
    const [spkList, setSpkList] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [warnings, setWarnings] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/setor-barang", { params: q ? { q } : {} });
            setItems(data);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        (async () => {
            const [s, v] = await Promise.all([api.get("/spk", { params: { status: "aktif" } }), api.get("/vendors")]);
            setSpkList(s.data); setVendors(v.data);
        })();
    }, []);
    useEffect(() => { const t = setTimeout(() => { load(); setPage(1); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

    const spkMap = useMemo(() => Object.fromEntries(spkList.map((s) => [s.id, s])), [spkList]);
    const vendorMap = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v.nama])), [vendors]);
    const selectedSpk = form.spk_id ? spkMap[form.spk_id] : null;

    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const pageItems = useMemo(() => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [items, page]);

    const openCreate = () => { setEditing(null); setForm({ ...emptyForm, items: [{ ...emptyItem }] }); setWarnings([]); setDialogOpen(true); };
    const openEdit = (row) => {
        setEditing(row);
        setForm({
            spk_id: row.spk_id, tanggal_setor: row.tanggal_setor,
            items: row.items.map((i) => ({ produk_id: i.produk_id, nama_produk_snapshot: i.nama_produk_snapshot, jumlah_pcs: i.jumlah_pcs, harga_per_pcs_snapshot: i.harga_per_pcs_snapshot })),
            catatan: row.catatan || "",
        });
        setWarnings([]); setDialogOpen(true);
    };

    const setItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
    const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem }] }));
    const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

    const pickProduk = (idx, produk_id) => {
        const p = selectedSpk?.items?.find((x) => x.produk_id === produk_id);
        if (p) setItem(idx, { produk_id, nama_produk_snapshot: p.nama_produk_snapshot, harga_per_pcs_snapshot: p.harga_per_pcs_sepakat });
    };

    const totals = form.items.map((i) => parseLocaleNum(i.jumlah_pcs || 0) * parseLocaleNum(i.harga_per_pcs_snapshot || 0));
    const totalNilai = totals.reduce((s, x) => s + x, 0);
    const totalPcs = form.items.reduce((s, i) => s + parseLocaleNum(i.jumlah_pcs || 0), 0);

    const save = async () => {
        if (!form.spk_id) return toast.error("SPK wajib dipilih");
        if (form.items.length === 0) return toast.error("Minimal 1 item produk");
        for (const it of form.items) {
            if (!it.produk_id) return toast.error("Pilih produk pada setiap item");
            if (parseLocaleNum(it.jumlah_pcs) <= 0) return toast.error("Jumlah pcs harus > 0");
        }
        setSubmitting(true);
        try {
            const payload = {
                spk_id: form.spk_id, tanggal_setor: form.tanggal_setor,
                items: form.items.map((i) => ({
                    produk_id: i.produk_id, nama_produk_snapshot: i.nama_produk_snapshot,
                    jumlah_pcs: parseInt(i.jumlah_pcs), harga_per_pcs_snapshot: parseFloat(i.harga_per_pcs_snapshot),
                })),
                catatan: form.catatan,
            };
            const res = editing
                ? await api.put(`/setor-barang/${editing.id}`, payload)
                : await api.post("/setor-barang", payload);
            if (res.data.warnings?.length > 0) {
                res.data.warnings.forEach((w) => toast.warning(w));
            } else {
                toast.success(editing ? "Setor Barang diperbarui" : "Setor Barang ditambahkan");
            }
            setDialogOpen(false); load();
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSubmitting(false); }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try { await api.delete(`/setor-barang/${confirmDelete.id}`); toast.success("Dipindahkan ke Riwayat Terhapus. Buka Riwayat untuk pulihkan."); setConfirmDelete(null); load(); }
        catch (e) { toast.error(errorMessage(e)); }
        finally { setDeleting(false); }
    };

    return (
        <div data-testid="setor-page">
            <PageHeader
                title="Setor Barang Jadi"
                description="Pencatatan setoran hasil produksi vendor"
                actions={canWrite() && <Button onClick={openCreate} data-testid="setor-add-button" className="rounded-lg gap-2 text-white font-medium" style={{ background: "var(--apple-blue)" }}><Plus className="w-4 h-4" /> Tambah Setor</Button>}
            />

            <div className="rounded-xl bg-white p-4 mb-4" style={{ border: "1px solid var(--border)" }}>
                <div className="relative max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                    <Input placeholder="Cari nomor setor..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="setor-search-input" className="pl-9 rounded-lg h-10" />
                </div>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-2">
                {loading ? (
                    <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--apple-blue)" }} /></div>
                ) : pageItems.length === 0 ? (
                    <div className="rounded-xl bg-white" style={{ border: "1px solid var(--border)" }}>
                        <EmptyState icon={Package} title="Belum ada setor" description="Belum ada setoran barang jadi." testid="setor-empty-mobile" />
                    </div>
                ) : pageItems.map((row) => {
                    const totalPcs = row.items?.reduce((s, i) => s + parseLocaleNum(i.jumlah_pcs || 0), 0) || 0;
                    return (
                        <div key={row.id} className="rounded-xl bg-white p-4" style={{ border: "1px solid var(--border)" }} data-testid={`setor-card-${row.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold text-sm" style={{ color: "var(--apple-blue)" }}>{row.nomor_setor}</div>
                                    <div className="text-xs truncate" style={{ color: "var(--text-primary)" }}>{vendorMap[row.vendor_id] || "-"}</div>
                                    <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>SPK: {row.nomor_spk_snapshot || "-"}</div>
                                </div>
                                <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-md border ${row.status_qc === "selesai_qc" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{row.status_qc === "selesai_qc" ? "Selesai QC" : "Belum QC"}</span>
                            </div>
                            <div className="flex items-center justify-between pt-2 mt-2" style={{ borderTop: "1px solid var(--border-soft)" }}>
                                <div className="text-xs"><span style={{ color: "var(--text-muted)" }}>{totalPcs.toLocaleString("id-ID")} pcs · </span><span className="font-semibold num-tabular" style={{ color: "var(--text-primary)" }}>{formatRupiah(row.total_nilai || 0)}</span></div>
                                <div className="flex items-center gap-1">
                                    {canWrite() && <button onClick={() => openEdit(row)} className="p-2 rounded-lg hover:bg-[var(--bg-subtle)]" style={{ color: "var(--text-secondary)" }}><Pencil className="w-4 h-4" /></button>}
                                    {canDelete() && <button onClick={() => setConfirmDelete(row)} className="p-2 rounded-lg hover:bg-[var(--apple-red-soft)]" style={{ color: "var(--apple-red)" }}><Trash2 className="w-4 h-4" /></button>}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="hidden md:block rounded-xl overflow-hidden bg-white" style={{ border: "1px solid var(--border)" }}>
                <Table data-testid="setor-data-table">
                    <TableHeader>
                        <TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">Nomor Setor</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">SPK</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">Vendor</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">Tanggal</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500 text-right">Total Pcs</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500 text-right">Total Nilai</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">QC</TableHead>
                            <TableHead className="text-right text-xs font-semibold uppercase text-slate-500 w-28">Aksi</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={8} className="py-12 text-center"><Loader2 className="w-5 h-5 text-teal-600 animate-spin mx-auto" /></TableCell></TableRow>
                        ) : pageItems.length === 0 ? (
                            <TableRow><TableCell colSpan={8} className="py-14 text-center"><div className="flex flex-col items-center gap-2 text-slate-400"><Inbox className="w-8 h-8" /><div className="text-sm">Belum ada setor</div></div></TableCell></TableRow>
                        ) : pageItems.map((row) => {
                            const totalPcs = row.items?.reduce((s, i) => s + parseLocaleNum(i.jumlah_pcs || 0), 0) || 0;
                            return (
                                <TableRow key={row.id} data-testid={`setor-row-${row.id}`} className="border-b border-slate-100/80 hover:bg-teal-50/40 transition-colors">
                                    <TableCell className="font-semibold text-teal-700">{row.nomor_setor}</TableCell>
                                    <TableCell className="text-sm text-slate-700">{row.nomor_spk_snapshot || "-"}</TableCell>
                                    <TableCell className="text-sm text-slate-700">{vendorMap[row.vendor_id] || "-"}</TableCell>
                                    <TableCell className="text-sm text-slate-600">{formatTanggal(row.tanggal_setor)}</TableCell>
                                    <TableCell className="text-sm text-slate-800 text-right num-tabular">{totalPcs.toLocaleString("id-ID")}</TableCell>
                                    <TableCell className="text-sm font-semibold text-slate-900 text-right num-tabular">{formatRupiah(row.total_nilai || 0)}</TableCell>
                                    <TableCell><span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-md border ${row.status_qc === "selesai_qc" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{row.status_qc === "selesai_qc" ? "Selesai QC" : "Belum QC"}</span></TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            {canWrite() && <button onClick={() => openEdit(row)} data-testid={`setor-edit-btn-${row.id}`} className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-teal-700"><Pencil className="w-4 h-4" /></button>}
                                            {canDelete() && <button onClick={() => setConfirmDelete(row)} data-testid={`setor-delete-btn-${row.id}`} className="p-2 rounded-lg text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {!loading && items.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/40">
                        <div className="text-xs text-slate-500">Menampilkan {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, items.length)} dari {items.length}</div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg h-8">Sebelumnya</Button>
                            <span className="text-xs text-slate-600 px-2">Hal. {page}/{totalPages}</span>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded-lg h-8">Selanjutnya</Button>
                        </div>
                    </div>
                )}
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="setor-modal-dialog" className="bg-white/95 backdrop-blur-2xl border-slate-200 rounded-2xl max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Setor Barang" : "Tambah Setor Barang Jadi"}</DialogTitle>
                        <DialogDescription>Pilih SPK, lalu pilih produk dari item SPK. Harga akan otomatis mengambil harga sepakat SPK.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label>SPK <span className="text-rose-600">*</span></Label>
                                <Select value={form.spk_id} onValueChange={(v) => setForm({ ...form, spk_id: v, items: [{ ...emptyItem }] })} disabled={!!editing}>
                                    <SelectTrigger data-testid="setor-form-spk" className="mt-1.5 rounded-xl"><SelectValue placeholder="Pilih SPK aktif" /></SelectTrigger>
                                    <SelectContent>
                                        {spkList.map((s) => <SelectItem key={s.id} value={s.id}>{s.nomor_spk} · {vendorMap[s.vendor_id]}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                {selectedSpk && <div className="text-xs text-slate-500 mt-1">Vendor: {vendorMap[selectedSpk.vendor_id]}</div>}
                            </div>
                            <div>
                                <Label>Tanggal Setor <span className="text-rose-600">*</span></Label>
                                <Input type="date" value={form.tanggal_setor} onChange={(e) => setForm({ ...form, tanggal_setor: e.target.value })} className="mt-1.5 rounded-xl" />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label>Item Produk <span className="text-rose-600">*</span></Label>
                                <Button type="button" size="sm" variant="outline" onClick={addItem} disabled={!form.spk_id} data-testid="setor-form-add-item" className="rounded-lg h-8 gap-1"><Plus className="w-3.5 h-3.5" /> Tambah Item</Button>
                            </div>
                            {!form.spk_id ? (
                                <div className="p-4 rounded-xl bg-amber-50/50 border border-amber-200 text-sm text-amber-800 flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4" /> Pilih SPK terlebih dahulu untuk melihat daftar produk
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {form.items.map((it, idx) => (
                                        <div key={idx} data-testid={`setor-form-item-${idx}`} className="grid grid-cols-12 gap-2 p-3 rounded-xl bg-slate-50/70 border border-slate-200/60">
                                            <div className="col-span-12 sm:col-span-5">
                                                <div className="text-[10px] uppercase text-slate-500 mb-1">Produk (dari SPK)</div>
                                                <Select value={it.produk_id} onValueChange={(v) => pickProduk(idx, v)}>
                                                    <SelectTrigger className="rounded-lg h-9 bg-white"><SelectValue placeholder="Pilih produk" /></SelectTrigger>
                                                    <SelectContent>
                                                        {selectedSpk?.items?.map((p) => <SelectItem key={p.produk_id} value={p.produk_id}>{p.nama_produk_snapshot} (target {p.target_pcs})</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="col-span-6 sm:col-span-3">
                                                <div className="text-[10px] uppercase text-slate-500 mb-1">Jumlah Pcs</div>
                                                <Input type="text" inputMode="decimal" value={it.jumlah_pcs} onChange={(e) => setItem(idx, { jumlah_pcs: e.target.value })} className="rounded-lg h-9 bg-white" />
                                            </div>
                                            <div className="col-span-6 sm:col-span-3">
                                                <div className="text-[10px] uppercase text-slate-500 mb-1">Harga/Pcs</div>
                                                <Input type="text" inputMode="decimal" value={it.harga_per_pcs_snapshot} onChange={(e) => setItem(idx, { harga_per_pcs_snapshot: e.target.value })} className="rounded-lg h-9 bg-white" />
                                            </div>
                                            <div className="col-span-12 sm:col-span-1 flex sm:justify-center sm:items-end">
                                                {form.items.length > 1 && <button type="button" onClick={() => removeItem(idx)} className="text-rose-600 hover:bg-rose-50 p-2 rounded-lg h-9"><X className="w-4 h-4" /></button>}
                                            </div>
                                            <div className="col-span-12 text-right text-xs text-slate-500 num-tabular">Subtotal: <span className="font-semibold text-slate-800">{formatRupiah(totals[idx])}</span></div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="mt-3 flex justify-end gap-6 text-sm">
                                <div className="text-slate-600">Total Pcs: <span className="font-bold num-tabular">{totalPcs.toLocaleString("id-ID")}</span></div>
                                <div className="text-slate-600">Total Nilai: <span className="font-bold text-teal-700 num-tabular">{formatRupiah(totalNilai)}</span></div>
                            </div>
                        </div>

                        <div>
                            <Label>Catatan</Label>
                            <Textarea value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} className="mt-1.5 rounded-xl" rows={2} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Batal</Button>
                        <Button onClick={save} disabled={submitting} data-testid="setor-modal-save-button" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menyimpan...</> : "Simpan"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="bg-white/95 backdrop-blur-2xl border-slate-200 rounded-2xl">
                    <AlertDialogHeader><AlertDialogTitle>Hapus Setor (ke Riwayat)</AlertDialogTitle><AlertDialogDescription><span className="font-semibold">{confirmDelete?.nomor_setor}</span> akan dipindahkan ke <b>Riwayat Terhapus</b> selama 30 hari dan bisa dipulihkan kapan saja. Lanjut?</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} disabled={deleting} data-testid="confirm-delete-button" className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl">{deleting ? "..." : "Hapus (ke Riwayat)"}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
